/*global swal*/
import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import SweetAlert from 'react-swal';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { getTimezoneOffset } from 'date-fns-tz';
import {
	addDays,
	addMilliseconds,
	addMinutes,
	differenceInDays,
	endOfDay,
	endOfMonth,
	endOfWeek,
	format,
	isAfter,
	isBefore,
	startOfDay,
	startOfMonth,
	startOfWeek,
	subDays,
	subMilliseconds,
} from 'date-fns';

import AddAppointmentModal from './AddAppointmentModal';
import ApplyTemplateModal from './ApplyTemplateModal';
import ProviderFilterModal from './ProviderFilterModal';
import EventActionModal from './EventActionModal';
import ScheduleAppointmentForNewPatientModal from './ScheduleAppointmentForNewPatientModal';
import ScheduleTAVModal from './ScheduleTAVModal';
import {
	getTemplates,
	applyTemplate,
	viewSchedule,
	createAppointmentSlot,
	deleteAppointmentSlot,
	getPracticeProviders,
	updateScheduleNote,
	createVisitAndScheduleAppointment,
	generatePatientCode,
} from './api';
import { createTextaVisit, isNumberMobile } from '../tracking-board/api';
import { getPatientName } from '../../../lib/util';
import { withNavigate } from '../../../components';
import { Link } from 'react-router-dom';

class Scheduling extends React.Component {
	isCalendarReffed = false;

	static propTypes = {
		user: PropTypes.object,
		showNotification: PropTypes.func,
		navigate: PropTypes.func,
	};

	constructor(props) {
		super(props);

		this.calendarRef = React.createRef();

		this.state = {
			templates: [],
			isLoading: false,
			isError: false,
			schedule: [],
			isLoadingSchedule: false,
			showApplyTemplateModal: false,
			templateId: 0,
			showAddAppointmentModal: false,
			showSwal: false,
			userShown: props.user.id,
			practiceProviders: [],
			showProviderFilterModal: false,
			showEventActionModal: false,
			showNewPatientAppointmentModal: false,
			showScheduleTAVModal: false,
		};

		this.localOffset = getTimezoneOffset(
			Intl.DateTimeFormat().resolvedOptions().timeZone
		);
		this.originalOffset = getTimezoneOffset(
			props.user.get('provider_practice').timezone
		);
	}

	async componentDidMount() {
		const { user } = this.props;
		const userId = user.get('id');
		this.setState({ isLoading: true, isLoadingSchedule: true, isError: false });

		const start = subDays(startOfWeek(new Date()), 1);
		const end = addDays(endOfWeek(new Date()), 1);

		try {
			const schedule = await viewSchedule(userId, start, end);
			this.setState({
				isLoadingSchedule: false,
				schedule,
				activeRange: {
					start,
					end,
				},
			});
		} catch (e) {
			// TODO
			this.props.showNotification({
				message: 'Unable to load schedule!',
				status: 'danger',
				timeout: 10000,
			});
			this.setState({ isLoadingSchedule: false, isError: true, error: e });
		}

		try {
			const templates = await getTemplates(userId);
			this.setState({ isLoading: false, templates });
		} catch (e) {
			this.setState({ isLoading: false, isError: true });
		}

		try {
			const practiceProviders = await getPracticeProviders(
				user.get('provider_practice_id')
			);
			const included = practiceProviders
				.map((provider) => provider.providerId)
				.includes(userId);
			this.setState({ practiceProviders, userShown: included ? userId : -1 });
		} catch (e) {
			this.setState({ isError: true });
		}
	}

	handleOpenApplyTemplateModal = () => {
		this.setState({ showApplyTemplateModal: true });
	};

	handleCloseApplyTemplateModal = () => {
		this.setState({ showApplyTemplateModal: false });
	};

	handleTemplateChosen = ({ target }) => {
		this.setState({ templateId: target.value });
	};

	handleApplyTemplate = async ({ templateId, duration }) => {
		const { user } = this.props;
		const { activeRange, schedule } = this.state;
		let slotDuration;
		let weekStartDate;
		this.setState({ isLoadingSchedule: true });
		if (this.localOffset < 0) {
			weekStartDate = startOfWeek(
				addMilliseconds(activeRange.end, this.originalOffset)
			);
		} else if (this.localOffset > 0) {
			weekStartDate = startOfWeek(
				addMilliseconds(activeRange.start, this.originalOffset)
			);
		}
		try {
			slotDuration = parseInt(duration, 10);
		} catch (e) {
			// TODO
		}
		try {
			await applyTemplate(
				user.get('id'),
				parseInt(templateId),
				weekStartDate,
				slotDuration
			);
			const thisWeeksSchedule = await viewSchedule(
				user.get('id'),
				weekStartDate,
				endOfWeek(weekStartDate)
			);
			this.setState({
				isLoadingSchedule: false,
				schedule: [...schedule, ...thisWeeksSchedule],
				showApplyTemplateModal: false,
				templateId: 0,
			});
			this.props.showNotification({
				message: 'Template applied to the week shown',
				status: 'success',
			});
		} catch (e) {
			this.setState({
				isLoadingSchedule: false,
				isError: true,
				error: e,
				showApplyTemplateModal: false,
				templateId: 0,
			});
			this.props.showNotification({
				message: 'Unable to apply a template to the week shown!',
				status: 'danger',
				timeout: 10000,
			});
		}
	};

	handleNavToPrevious = async () => {
		this.setState({ isLoadingSchedule: true });
		this.calendarRef.current.getApi().prev();
		this.loadActiveRange();
	};

	handleNavToNext = () => {
		this.setState({ isLoadingSchedule: true });
		this.calendarRef.current.getApi().next();
		this.loadActiveRange();
	};

	handleToday = () => {
		this.setState({ isLoadingSchedule: true });
		this.calendarRef.current.getApi().today();
		this.loadActiveRange();
	};

	handleViewChange = async (view) => {
		const { currentDate } = this.calendarRef.current.getApi().state;
		const [, range] = view.split('Grid');

		let start = startOfWeek(currentDate);
		let end = endOfWeek(new Date());
		if (range === 'Month') {
			start = startOfMonth(currentDate);
			end = endOfMonth(currentDate);
		} else if (range === 'Day') {
			start = startOfDay(currentDate);
			end = endOfDay(currentDate);
		}
		const newActiveRange = {
			start: addMilliseconds(start, this.localOffset),
			end: addMilliseconds(end, this.localOffset),
		};

		this.setState({ isLoadingSchedule: true });
		const schedule = await viewSchedule(
			this.props.user.get('id'),
			subDays(newActiveRange.start, 1),
			addDays(newActiveRange.end, 1)
		);
		this.setState({
			schedule,
			isLoadingSchedule: false,
			activeRange: newActiveRange,
		});

		this.calendarRef.current.getApi().changeView(view);
	};

	loadActiveRange = async () => {
		const { user } = this.props;
		const { activeRange } = this.calendarRef.current.getApi().state.dateProfile;
		const schedule = await viewSchedule(
			user.get('id'),
			subDays(activeRange.start, 1),
			addDays(activeRange.end, 1)
		);
		this.setState({
			schedule,
			isLoadingSchedule: false,
			activeRange,
		});
	};

	handleEventClick = ({ event, jsEvent }) => {
		jsEvent.preventDefault();
		this.setState({
			showEventActionModal: true,
			selectedEvent: {
				...event.extendedProps,
				id: event.extendedProps.blockId,
			},
		});
	};

	handleCancelVisit = (evisit = {}) => {
		this.setState((currState) => ({
			showSwal: true,
			showEventActionModal: false,
			appointmentToDelete: { ...currState.selectedEvent, evisit },
			selectedEvent: undefined,
		}));
	};

	handleOpenVisit = () => {
		const { selectedEvent } = this.state;
		this.setState({
			selectedEvent: undefined,
			showEventActionModal: false,
		});

		this.props.navigate(`/evisit/${selectedEvent.evisit_id}`);
	};

	handleDeleteAppointmentSlot = async (isConfirm) => {
		const { appointmentToDelete, schedule } = this.state;
		if (isConfirm) {
			try {
				const { data } = await deleteAppointmentSlot(
					this.props.user.get('id'),
					appointmentToDelete.id
				);

				if (data && data.status && data.status.cancelled_ind) {
					// remove event from calendar
					const updatedSchedule = schedule.filter(
						(event) => event.id != appointmentToDelete.id
					);
					this.setState({ schedule: updatedSchedule });
					this.props.showNotification({
						message: 'Appointment slot removed',
						status: 'success',
					});
				}
			} catch (e) {
				this.props.showNotification({
					message: 'Unable to remove the selected Appointment Slot!',
					status: 'danger',
					timeout: 10000,
				});
			}

			this.setState({ showSwal: false, appointmentToDelete: null });
			swal.close();
		} else {
			this.setState({ showSwal: false, appointmentToDelete: null });
			swal.close();
		}
	};

	handleDateClick = (dateClickInfo) => {
		const { date } = dateClickInfo;
		this.setState({
			showAddAppointmentModal: true,
			appointment: {
				startDate: date,
			},
		});
	};

	handleCloseAddAppointmentModal = () => {
		this.setState({
			showAddAppointmentModal: false,
			appointment: null,
		});
	};

	handleOpenProviderFilterModal = () => {
		this.setState({ showProviderFilterModal: true });
	};

	handleCloseProviderFilterModal = () => {
		this.setState({ showProviderFilterModal: false });
	};

	handleCloseEventActionModal = () => {
		this.setState({
			showEventActionModal: false,
			selectedEvent: undefined,
		});
	};

	handleSaveEvent = async ({ notes }) => {
		const { selectedEvent, schedule } = this.state;
		this.setState({
			showEventActionModal: false,
			selectedEvent: undefined,
		});
		try {
			await updateScheduleNote({
				providerId: selectedEvent.provider_id,
				notes,
				appointmentId: selectedEvent.id,
			});
			// update local copy of schedule
			const indexOfSelectedEvent = schedule.findIndex(
				(slot) => slot.id === selectedEvent.id
			);
			schedule[indexOfSelectedEvent].notes = notes;
			this.setState({
				schedule: [...schedule],
			});
			// show success notification
			this.props.showNotification({
				status: 'success',
				message: 'Time slot updated successfully!',
			});
		} catch (e) {
			// show failure notification
			this.props.showNotification({
				status: 'danger',
				message: 'Unable to update time slot!',
				timeout: 10000,
			});
		}
	};

	handleSetUserShown = (userShown) => {
		this.setState({
			userShown,
			showProviderFilterModal: false,
		});
	};

	handleAddAppointmentSlot = async (startDateString, durationInMinutes) => {
		const { schedule } = this.state;
		const startDate = subMilliseconds(
			addMilliseconds(new Date(startDateString), this.localOffset),
			this.originalOffset
		);
		const endDate = addMinutes(startDate, durationInMinutes);

		try {
			const { data } = await createAppointmentSlot(
				this.props.user.get('id'),
				startDate,
				endDate
			);
			if (data && data.status) {
				this.setState({ schedule: [...schedule, data.status] });
				this.props.showNotification({
					message: 'Appointment slot added',
					status: 'success',
				});
			}
			this.handleCloseAddAppointmentModal();
		} catch (e) {
			this.props.showNotification({
				message: 'Unable to add a new Appointment Slot!',
				status: 'danger',
				timeout: 10000,
			});
			this.handleCloseAddAppointmentModal();
		}
	};

	handleNavToTemplateBuilder = () => {
		this.props.navigate('/scheduling/template-builder');
	};

	getEventClassNames = (event) => {
		const classNames = [];
		if (event.available_ind) {
			classNames.push('available');
		} else if (!event.evisit_id && !event.available_ind) {
			classNames.push('unavailable');
		}
		return classNames;
	};

	getEventTitle = (event) => {
		const clickMessage = '[click to view details]';
		if (event.evisit_id) {
			return `[Scheduled] ${clickMessage}`;
		} else if (!event.available_ind) {
			return `[Unavailable] ${clickMessage}`;
		}
		return `[Available] ${clickMessage}`;
	};

	handleScheduleAppointment = async (evisit) => {
		this.setState({ isCreatingScheduledAppointment: true });
		try {
			const { schedule } = this.state;
			const appointment = await createVisitAndScheduleAppointment(
				this.props.user.id,
				evisit
			);
			const apptIndex = schedule.findIndex(
				(appt) => appt.id === appointment.id
			);
			if (apptIndex >= 0) {
				schedule[apptIndex] = appointment;
			}
			this.setState({
				isCreatingScheduledAppointment: false,
				showEventActionModal: false,
				schedule: [...schedule],
			});
			this.props.showNotification({
				status: 'success',
				message: 'Appointment scheduled successfully',
			});
		} catch (err) {
			this.setState({
				isCreatingScheduledAppointment: false,
				showEventActionModal: false,
			});
			this.props.showNotification({
				message: 'Unable to schedule an appointment!',
				status: 'danger',
				timeout: 10000,
			});
		}
	};

	handleOpenScheduleAppointmentForNewPatientModal = () => {
		this.setState({
			showEventActionModal: false,
			showNewPatientAppointmentModal: true,
		});
	};

	handleCloseScheduleAppointmentForNewPatientModal = () => {
		this.setState({
			showNewPatientAppointmentModal: false,
			inviteCode: null,
			selectedEvent: undefined,
		});
	};

	handleCreateNewPatientWithAppointment = async (data) => {
		const { schedule, selectedEvent } = this.state;
		const userId = this.props.user.get('id');
		this.setState({ generatingPatientCode: true });
		try {
			const inviteCode = await generatePatientCode(userId, data);
			const reloadFrom = addMilliseconds(
				startOfDay(new Date(selectedEvent.scheduled_start_dttm))
			);
			const reloadTo = endOfDay(reloadFrom);
			const updatedSchedule = await viewSchedule(userId, reloadFrom, reloadTo);
			const updatedEvent = updatedSchedule.find(
				({ id }) => id === selectedEvent.id
			);
			if (updatedEvent) {
				const indexOfSelectedEvent = schedule.findIndex(
					({ id }) => id === selectedEvent.id
				);
				if (indexOfSelectedEvent >= 0) {
					schedule[indexOfSelectedEvent] = updatedEvent;
				}
			}
			this.setState({
				generatingPatientCode: false,
				inviteCode,
				schedule: [...schedule],
			});
			this.props.showNotification({
				message: 'Invite code created',
				status: 'success',
			});
		} catch (err) {
			this.setState({ generatingPatientCode: false });
			this.props.showNotification({
				message: 'There was a problem generating an invite code!',
				status: 'danger',
				timeout: 10000,
			});
		}
	};

	handleOpenScheduleTAVModal = () => {
		this.setState({
			showEventActionModal: false,
			showScheduleTAVModal: true,
		});
	};

	handleCloseScheduleTAVModal = () => {
		this.setState({
			showScheduleTAVModal: false,
			selectedEvent: undefined,
		});
	};

	handleVerifyMobileNumber = async (number) => {
		const userId = this.props.user.get('id');
		const phoneData = await isNumberMobile(userId, number);
		this.setState({ phoneData });
	};

	handleCreateScheduledTAV = async (visitData) => {
		const { schedule, selectedEvent } = this.state;
		const userId = this.props.user.get('id');
		try {
			this.setState({ showScheduleTAVModal: false });
			const { data } = await createTextaVisit(userId, visitData);
			const reloadFrom = addMilliseconds(
				startOfDay(new Date(selectedEvent.scheduled_start_dttm))
			);
			const reloadTo = endOfDay(reloadFrom);
			const updatedSchedule = await viewSchedule(userId, reloadFrom, reloadTo);
			const updatedEvent = updatedSchedule.find(
				({ id }) => id === selectedEvent.id
			);
			if (updatedEvent) {
				const indexOfSelectedEvent = schedule.findIndex(
					({ id }) => id === selectedEvent.id
				);
				if (indexOfSelectedEvent >= 0) {
					schedule[indexOfSelectedEvent] = updatedEvent;
				}
			}
			if (data && data.cb === 'visit created') {
				this.props.showNotification({
					status: 'success',
					message: 'Text-a-Visit created.',
				});
			} else {
				this.props.showNotification({
					message: 'Unable to create Text-a-Visit!',
					status: 'danger',
					timeout: 10000,
				});
			}
			this.setState({ schedule: [...schedule] });
		} catch (err) {
			this.props.showNotification({
				message: 'Unable to create Text-a-Visit!',
				status: 'danger',
				timeout: 10000,
			});
		}
	};

	render() {
		const { user } = this.props;
		const {
			schedule,
			activeRange,
			showApplyTemplateModal,
			templates,
			showAddAppointmentModal,
			showEventActionModal,
			appointment,
			showSwal,
			appointmentToDelete,
			userShown,
			practiceProviders,
			showProviderFilterModal,
			selectedEvent,
			showNewPatientAppointmentModal,
			generatingPatientCode,
			inviteCode,
			isLoadingSchedule,
			showScheduleTAVModal,
			phoneData,
		} = this.state;
		const userId = user.get('id');

		let scheduleShown;
		let providerFilterLabel = 'All Providers';
		if (userShown != -1) {
			scheduleShown = schedule.filter((slot) => slot.provider_id === userShown);
			if (userShown == userId) {
				providerFilterLabel = 'My Schedule';
				scheduleShown = schedule.filter(
					(slot) => slot.provider_id === userShown
				);
			} else {
				const provider = practiceProviders.find(
					(p) => p.providerId === userShown
				);
				if (provider) {
					providerFilterLabel = `${provider.name_first} ${provider.name_last}, ${provider.credentials}`;
				}
			}
		} else {
			scheduleShown = schedule.slice();
		}

		const customButtons = {
			myToday: {
				text: 'Today',
				click: this.handleToday,
			},
			myPrev: {
				icon: 'chevron-left',
				click: this.handleNavToPrevious,
			},
			myNext: {
				icon: 'chevron-right',
				click: this.handleNavToNext,
			},
			templateBuilder: {
				text: 'Template Builder',
				click: this.handleNavToTemplateBuilder,
			},
			providerFilter: {
				text: `${providerFilterLabel} ▾`,
				click: this.handleOpenProviderFilterModal,
			},
			dayGridMonth: {
				text: 'Month',
				click: () => this.handleViewChange('dayGridMonth'),
			},
			timeGridWeek: {
				text: 'Week',
				click: () => this.handleViewChange('timeGridWeek'),
			},
			timeGridDay: {
				text: 'Day',
				click: () => this.handleViewChange('timeGridDay'),
			},
		};

		if (this.calendarRef && this.calendarRef.current && activeRange) {
			const daysInRange = differenceInDays(activeRange.end, activeRange.start);
			if (daysInRange > 1 && daysInRange < 14) {
				const thisRangeSchedule = scheduleShown.filter(
					(block) =>
						isAfter(new Date(block.scheduled_start_dttm), activeRange.start) &&
						isBefore(new Date(block.scheduled_start_dttm), activeRange.end)
				);
				if (thisRangeSchedule && thisRangeSchedule.length === 0) {
					customButtons.applyTemplate = {
						text: 'Apply Template',
						click: this.handleOpenApplyTemplateModal,
					};
				}
			}
		}

		const options = [{ value: 0, label: 'Choose...' }];
		templates.forEach((template) => {
			options.push({ value: template.id, label: template.template_name });
		});

		let apptStartDttm, apptEndDttm;
		if (appointmentToDelete) {
			apptStartDttm = format(
				new Date(appointmentToDelete.scheduled_start_dttm),
				'PPPp'
			);
			apptEndDttm = format(
				new Date(appointmentToDelete.scheduled_end_dttm),
				'PPPp'
			);
		}

		const defaultDuration =
			this.props.user.get('provider_practice').scheduled_appointment_length;

		let owner;
		if (selectedEvent) {
			owner = practiceProviders.find(
				(p) => p.providerId === selectedEvent.provider_id
			);
		}

		let deleteMessage;
		if (appointmentToDelete && appointmentToDelete.evisit_id) {
			deleteMessage = `Cancel you appointment with ${getPatientName(
				appointmentToDelete.evisit
			)}?`;
		} else {
			deleteMessage = `Delete this Appointment Slot (${apptStartDttm} until ${apptEndDttm})?`;
		}

		schedule.map((slot) => ({
			...slot,
			scheduled_start_dttm: subMilliseconds(
				addMilliseconds(
					new Date(slot.scheduled_start_dttm),
					this.originalOffset
				),
				this.localOffset
			).toISOString(),
			scheduled_end_dttm: subMilliseconds(
				addMilliseconds(new Date(slot.scheduled_end_dttm), this.originalOffset),
				this.localOffset
			).toISOString(),
		}));

		return (
			<div className="content-wrapper">
				<ProviderFilterModal
					isOpen={showProviderFilterModal}
					onCancel={this.handleCloseProviderFilterModal}
					onOk={this.handleSetUserShown}
					userShown={userShown}
					providers={practiceProviders}
				/>
				<ApplyTemplateModal
					isOpen={showApplyTemplateModal}
					templateOptions={options}
					onClose={this.handleCloseApplyTemplateModal}
					onApplyTemplate={this.handleApplyTemplate}
					defaultDuration={defaultDuration}
				/>
				<AddAppointmentModal
					timezone={this.props.user.get('provider_practice').timezone}
					isOpen={showAddAppointmentModal}
					onCancel={this.handleCloseAddAppointmentModal}
					appointment={appointment}
					onAdd={this.handleAddAppointmentSlot}
				/>
				<EventActionModal
					isOpen={showEventActionModal}
					onClose={this.handleCloseEventActionModal}
					event={selectedEvent}
					isOwner={!!selectedEvent}
					onCancel={this.handleCancelVisit}
					onOpenVisit={this.handleOpenVisit}
					userId={userId}
					owner={owner}
					onUpdateEvent={this.handleSaveEvent}
					onScheduleAppointment={this.handleScheduleAppointment}
					timeZone={this.props.user.get('provider_practice').timezone}
					onScheduleAppointmentForNewPatient={
						this.handleOpenScheduleAppointmentForNewPatientModal
					}
					onScheduleTAV={this.handleOpenScheduleTAVModal}
				/>
				<ScheduleAppointmentForNewPatientModal
					isOpen={showNewPatientAppointmentModal}
					onClose={this.handleCloseScheduleAppointmentForNewPatientModal}
					event={selectedEvent}
					timeZone={this.props.user.get('provider_practice').timezone}
					userId={userId}
					provider={owner}
					onCreatePatientWithAppointment={
						this.handleCreateNewPatientWithAppointment
					}
					isGeneratingInviteCode={generatingPatientCode}
					inviteCode={inviteCode}
				/>
				<ScheduleTAVModal
					isOpen={showScheduleTAVModal}
					onClose={this.handleCloseScheduleTAVModal}
					event={selectedEvent}
					timeZone={this.props.user.get('provider_practice').timezone}
					userId={userId}
					provider={owner}
					onSubmit={this.handleCreateScheduledTAV}
					onVerifyMobileNumber={this.handleVerifyMobileNumber}
					phoneData={phoneData}
				/>
				<SweetAlert
					isOpen={showSwal}
					type="warning"
					title="Are you sure?"
					text={deleteMessage}
					showCancelButton
					confirmButtonColor="#DD6B55"
					confirmButtonText="Yes"
					closeOnConfirm={false}
					closeOnCancel={false}
					callback={this.handleDeleteAppointmentSlot}
				/>
				<ol className="breadcrumb">
					<li>
						<Link to="/">Home</Link>
					</li>
					<li className="active">Scheduling</li>
				</ol>
				<div
					className={cn('panel', 'panel-default', {
						whirl: isLoadingSchedule,
						traditional: isLoadingSchedule,
					})}
				>
					<div className="panel-body">
						<FullCalendar
							ref={this.calendarRef}
							plugins={[
								dayGridPlugin,
								timeGridPlugin,
								// momentTimezonePlugin,
								interactionPlugin,
							]}
							defaultView="timeGridWeek"
							eventClick={this.handleEventClick}
							dateClick={this.handleDateClick}
							allDaySlot={false}
							height={700}
							// timeZone={this.props.user.get('provider_practice').timezone}
							events={scheduleShown.map((block) => ({
								...block,
								blockId: block.id,
								start: subMilliseconds(
									addMilliseconds(
										new Date(block.scheduled_start_dttm),
										this.originalOffset
									),
									this.localOffset
								).toISOString(), //block.scheduled_start_dttm,
								end: subMilliseconds(
									addMilliseconds(
										new Date(block.scheduled_end_dttm),
										this.originalOffset
									),
									this.localOffset
								).toISOString(), //block.scheduled_end_dttm,
								title: this.getEventTitle(block),
								url: block.evisit_id
									? `#evisit/${block.evisit_id}`
									: `#scheduling/${block.id}`,
								classNames: this.getEventClassNames(block),
							}))}
							header={{
								left: 'myPrev,myNext myToday templateBuilder',
								center: 'title',
								right:
									'applyTemplate providerFilter dayGridMonth,timeGridWeek,timeGridDay',
							}}
							buttonText={{
								today: 'Today',
								week: 'Week',
								day: 'Day',
								month: 'Month',
							}}
							customButtons={customButtons}
						/>
					</div>
				</div>
			</div>
		);
	}
}

export default withNavigate(Scheduling);
